"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { PersonIcon } from "@radix-ui/react-icons";
import { Loader2, Mail } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { Dispatch, SetStateAction } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";
import { createClient } from "../../../../utils/supabase/client";
import useAuthModal from "../../hooks/use-auth-modal";
import SignInWithGoogle from "../sign-in-with-google";

interface AuthFormProps {
  isSignUp?: boolean;
  setIsSignUp: Dispatch<SetStateAction<boolean>>;
  onSuccess: (email: string) => void; // Add this
}

const formSchema = z.object({
  email: z
    .string()
    .email({
      message: "Your email must be a valid email address.",
    })
    .min(5, {
      message: "Your email must be a valid email address",
    }),
  name: z.string(),
});

const AuthForm = ({ isSignUp, setIsSignUp, onSuccess }: AuthFormProps) => {
  const _router = useRouter();
  const _searchParams = useSearchParams();
  const { onClose: _onClose } = useAuthModal();
  const pathname = usePathname();
  const t = useI18n();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      name: "",
    },
  });

  const isLoading = form.formState.isSubmitting;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (isSignUp && values.name.length < 1) {
      form.setError("name", {
        type: "manual",
        message: t("required"),
      });
      return;
    }
    const supabase = createClient();
    const { error } = await supabase.auth.signInWithOtp({
      email: values.email,
      options: {
        data: {
          name: values.name,
        },
        shouldCreateUser: isSignUp,
      },
    });
    if (error) {
      console.error("Network or fetch error:", error.message);
      form.setError("email", {
        type: "manual",
        message:
          error.message === "Signups not allowed for otp"
            ? t("auth.noAccountFound")
            : t("auth.errorOccurred"),
      });
      return;
    }
    onSuccess(values.email);
  };

  return (
    <div className="flex flex-col gap-6">
      <SignInWithGoogle redirectTo={pathname} />
      <div className="relative w-full mt-2">
        <hr className="w-full" />
        <div className="relative flex justify-center text-xs uppercase -mt-2">
          <span className="bg-background px-2 text-muted-foreground">
            {t("auth.orEmail")}
          </span>
        </div>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6 w-full"
        >
          {isSignUp && (
            <FormField
              name="name"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("auth.name")}</FormLabel>
                  <div className="relative flex items-center">
                    <PersonIcon className="size-4 absolute left-2" />
                    <FormControl>
                      <Input
                        placeholder={t("auth.namePlaceholder")}
                        className={cn("pl-8")}
                        {...field}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("email")}</FormLabel>
                <div className="relative flex items-center">
                  <Mail className="size-4 absolute left-2" />
                  <FormControl>
                    <Input
                      placeholder={t("auth.emailPlaceholder")}
                      type="email"
                      className={cn("pl-8")}
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <p className="text-xs font-normal text-muted-foreground text-center">
            {t("auth.termsAndPrivacy")}{" "}
            <a
              target="_blank"
              href="https://studygenie.io/terms"
              className="font-semibold"
              rel="noreferrer"
            >
              {t("auth.termsOfService")}
            </a>{" "}
            {t("auth.and")}{" "}
            <a
              target="_blank"
              href="https://studygenie.io/privacy"
              className="font-semibold"
              rel="noreferrer"
            >
              {t("auth.privacyPolicy")}
            </a>
            .
          </p>

          <div className="flex flex-col gap-2">
            <Button className="w-full" type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-4 h-4 w-4 animate-spin" />}
              {isSignUp ? t("auth.signUp") : t("auth.signIn")}
            </Button>
            <Button
              onClick={() => setIsSignUp((prev) => !prev)}
              variant={"outline"}
              className="w-full"
              type="button"
              disabled={isLoading}
            >
              {isSignUp
                ? t("auth.alreadyHaveAccount")
                : t("auth.newToStudygenie")}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default AuthForm;
